import { useContext, useState, type ReactNode } from "react";
import { Attachment } from "iconoir-react";
import { MacrosContext } from "../../../utils/MacrosContext";
import ClearSmartAssistSessionModal from "../../noteList/ClearSmartAssistSessionModal";
import { useSmartAssist } from "../core/useSmartAssist";
import type { SmartAssistQuickAction, UseSmartAssistOptions } from "../core/types";
import SmartAssistConversation, {
  type SmartAssistEmptyState,
} from "../content/SmartAssistConversation";
import SmartAssistFooter from "../content/SmartAssistFooter";
import SmartAssistHeader from "../content/SmartAssistHeader";
import SmartAssistReply from "../content/SmartAssistReply";
import SmartAssistReplyActions from "../content/SmartAssistReplyActions";

const SMART_CHART_HELP_TEXT =
  "Smart Assist has access to the note content, patient summary, diarized transcript, note citations, active problems list, medications, allergies, vitals, and labs. It can help you reformat or summarize this note, summarize uploaded documents/notes, or help you create a custom prompt. Smart Assist is not a substitute for your own clinical judgment. Always review and edit the output before using it in the patient record.";
const SMART_CHART_DESCRIPTION =
  "Smart Assist can help modify/reformat this note, summarize uploaded documents/notes, or help you create a custom prompt";
const SMART_CHART_EMPTY_STATE: SmartAssistEmptyState = {
  title: "How can I help with this note?",
  body: "Smart Assist works with your dot phrases and any Smart Chart fields you select. Choose the data objects you would like it to reference.",
};

export interface SmartAssistPanelProps extends UseSmartAssistOptions {
  /** Fixed height for the Smart Chart tab; otherwise the panel fills its container. */
  embedded?: boolean;
  onMinimize?: () => void;
  minimizeControlsId?: string;
  onUseAsNote?: (reply: string) => void;
  onInsertAsAdditionalContext?: (reply: string) => void;
  quickPrompts?: SmartAssistQuickAction[];
  showCreatePrompt?: boolean;
  helpText?: string;
  helpContent?: ReactNode;
  /** Pass null to omit the line under the heading. */
  description?: ReactNode;
  descriptionId?: string;
  emptyState?: SmartAssistEmptyState;
  footerActions?: ReactNode;
}

interface MacrosContextValue {
  fetchUserMacros?: () => unknown;
}

function SmartAssistPanelContent({
  embedded = false,
  onMinimize,
  minimizeControlsId,
  onUseAsNote,
  onInsertAsAdditionalContext,
  quickPrompts,
  showCreatePrompt = true,
  helpText = SMART_CHART_HELP_TEXT,
  helpContent,
  description = SMART_CHART_DESCRIPTION,
  descriptionId,
  emptyState = SMART_CHART_EMPTY_STATE,
  footerActions,
  ...options
}: SmartAssistPanelProps) {
  const { fetchUserMacros } = (useContext(MacrosContext) || {}) as MacrosContextValue;
  const [isClearConfirmationOpen, setIsClearConfirmationOpen] = useState(false);
  const assist = useSmartAssist(options);

  const replyActions = (
    <SmartAssistReplyActions
      reply={assist.reply}
      requestKind={assist.requestKind}
      isIntervalHistoryReply={assist.isIntervalHistoryReply}
      isReplyCopied={assist.isReplyCopied}
      onCopied={assist.markReplyCopied}
      onUseAsNote={onUseAsNote}
      onInsertAsAdditionalContext={onInsertAsAdditionalContext}
    />
  );

  return (
    <div
      aria-label="Smart Assist file drop area"
      {...assist.fileDropHandlers}
      className={`relative flex min-h-0 flex-col overflow-x-hidden overflow-y-auto rounded-2xl border bg-white shadow-[0_12px_32px_rgba(15,23,42,0.08)] ${
        assist.isFileDragActive ? "border-violet-400" : "border-sky-100"
      } ${embedded ? "h-[min(62vh,44rem)] min-h-[30rem]" : "h-full"}`}
    >
      {assist.isFileDragActive && (
        <div className="pointer-events-none absolute inset-2 z-50 flex items-center justify-center rounded-xl border-2 border-dashed border-violet-400 bg-violet-50/95 text-sm font-semibold text-accent1 shadow-sm">
          <div className="flex items-center gap-2">
            <Attachment className="h-5 w-5 text-violet-600" />
            Drop files to attach
          </div>
        </div>
      )}
      <SmartAssistHeader
        helpText={helpText}
        helpContent={helpContent}
        description={description}
        descriptionId={descriptionId}
        onMinimize={onMinimize}
        minimizeControlsId={minimizeControlsId}
        historyError={assist.historyError}
        canClearHistory={assist.canClearHistory}
        onClearHistory={() => setIsClearConfirmationOpen(true)}
      />
      <SmartAssistConversation
        displayedConversation={assist.displayedConversation}
        reply={assist.reply}
        pendingStatus={assist.pendingStatus}
        shouldShowPromptWorkflowStatus={assist.shouldShowPromptWorkflowStatus}
        promptWorkflowStep={assist.promptWorkflowStep}
        activeQuickAction={assist.activeQuickAction}
        emptyState={emptyState}
      >
        {assist.reply && (
          <SmartAssistReply
            reply={assist.reply}
            isAwaitingResponse={assist.isAwaitingResponse}
            requestKind={assist.requestKind}
            citations={assist.citations}
            promptReformatSummary={assist.promptReformatSummary}
            onMacroSaved={fetchUserMacros}
            actions={replyActions}
          />
        )}
      </SmartAssistConversation>
      <SmartAssistFooter
        {...assist}
        quickPrompts={quickPrompts}
        showCreatePrompt={showCreatePrompt}
        footerActions={footerActions}
      />
      {isClearConfirmationOpen && (
        <ClearSmartAssistSessionModal
          onClose={() => setIsClearConfirmationOpen(false)}
          onConfirm={assist.clearHistory}
        />
      )}
    </div>
  );
}

/**
 * Gov Smart Assist host. Keyed by patient so a patient switch can never show the
 * previous patient's conversation while the new session loads.
 */
export default function SmartAssistPanel(props: SmartAssistPanelProps) {
  return <SmartAssistPanelContent key={props.patientId} {...props} />;
}
